import React, { useState, useEffect, useMemo, useRef } from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { injectIntl } from '../../util/reactIntl';
import { isScrollingDisabled, manageDisableScrolling } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn } from '../../components';
import { useConfiguration } from '../../context/configurationContext';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { getLocaleFromUrl } from '../../constants/locales';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import HeroSection from './HeroSection';
import ListingSection from './ListingSection';
import CategorySection from './CategorySection';
import RegionDisciplineSection from './RegionDisciplineSection';
import ArticleSection from './ArticleSection';
import CTASection from './CTASection';
import DescriptionSection from './DescriptionSection';
import FaqSection from './FaqSection';
import OutdoorSkeletonLoader from './OutdoorSkeletonLoader';
import { 
  ListingSectionSkeleton, 
  CategorySectionSkeleton, 
  RegionDisciplineSectionSkeleton, 
  ArticleSectionSkeleton 
} from './SkeletonLoaders';

import { fetchCarouselData, queryPopularListings, clearCSRData } from './SeoPage.duck';
import {
  getComponentStructure,
  filterRelatedPages,
  getlistingFilters,
  getRequiredPageTypes,
} from './SeoPage.helpers';
import css from './SeoPage.module.css';

const componentMap = {
  'component.hero-section': HeroSection,
  'component.listing-section': ListingSection,
  'component.category-section': CategorySection,
  'component.region-discipline-section': RegionDisciplineSection,
  'component.article-section': ArticleSection,
  'component.description-section': DescriptionSection,
  'component.faq-section': FaqSection,
  'component.cta-section': CTASection,
  // Skeleton components for progressive loading
  'component.listing-section-skeleton': ListingSectionSkeleton,
  'component.category-section-skeleton': CategorySectionSkeleton,
  'component.region-discipline-section-skeleton': RegionDisciplineSectionSkeleton,
  'component.article-section-skeleton': ArticleSectionSkeleton,
};

export const SeoPageComponent = props => {
  const {
    seoPageData,
    fetchError,
    fetchInProgress,
    seoData,
    popularListings,
    querySeoInProgress,
    queryPopularListingsInProgress,
    scrollingDisabled,
    currentUser,
    onFetchSeoPageLinkData,
    onQueryPopularListings,
    onClearCSRData,
    localizedUrlMappings, // URL mappings for language switching
    intl, // Add intl for localization
  } = props;

  const config = useConfiguration();
  const { pathname } = useLocation() || '';

  // ✅ CLIENT-SIDE PARTNER SUBDOMAIN VALIDATION
  // Check if we're on a partner subdomain and block access
  const [isPartnerSubdomain, setIsPartnerSubdomain] = useState(false);

  useEffect(() => {
    // Only run on client-side
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      
      // Core domains: crion.org, test.crion.org, localhost
      const isCoreDomain =
        (parts.length === 2 && parts[0] === 'crion' && parts[1] === 'org') ||
        (parts.length === 3 && parts[0] === 'test' && parts[1] === 'crion' && parts[2] === 'org') ||
        (parts.length === 1 &&
          (parts[0] === 'localhost' ||
            parts[0].startsWith('localhost') ||
            parts[0] === '127.0.0.1' ||
            parts[0] === '::1' ||
            parts[0] === '[::1]'));

      if (!isCoreDomain) {
        // This is a partner subdomain - block access
        setIsPartnerSubdomain(true);
      } else {
        setIsPartnerSubdomain(false);
      }
    }
  }, []); // Run once on mount

  const [filteredData, setFilteredData] = useState({
    listing: [],
    regionDiscipline: [],
    article: [],
    category: [],
  });

  // Progressive loading state management
  const [csrLoadingState, setCsrLoadingState] = useState({
    listings: true,      // Sharetribe listings
    carousels: true,     // Strapi carousels (category, region-discipline, articles)
  });

  const pageData = seoPageData?.attributes || seoPageData;
  const {
    pageType,
    title,
    description,
    image,
    section = [],
    faq = [],
    tag = [],
    schema,
  } = pageData || {};

  const urlParts = pathname.split('/').filter(Boolean);

  // Get current locale from URL (more reliable than intl.locale during SSR)
  const currentLocale = getLocaleFromUrl(pathname);

  // Title and description are always available from Strapi (extracted from section[0])
  const heroTitle = title || '';
  const heroDescription = description || '';
  
  // Image handling for social sharing
  // Use Strapi image if available, otherwise fall back to branding defaults
  const { branding } = config;
  const { facebookImage, twitterImage } = branding || {};
  
  const strapiImageUrl = image?.url || image?.data?.attributes?.url;
  const pageImage = strapiImageUrl 
    ? (strapiImageUrl.startsWith('http') ? strapiImageUrl : `${config.marketplaceRootURL}${strapiImageUrl}`)
    : null;
  
  const schemaImage = pageImage || facebookImage;
  const twitterSchemaImage = pageImage || twitterImage;

  const tagString = JSON.stringify(tag);
  const urlPartsString = JSON.stringify(urlParts);

  // Track when SSR data changes (new page/language) and clear old CSR data
  const previousSeoPageDataRef = useRef(null);
  const previousLocaleRef = useRef(currentLocale);
  const previousPathnameRef = useRef(pathname);
  
  useEffect(() => {
    // When pathname changes, it means we're on a new page/language
    if (previousPathnameRef.current && previousPathnameRef.current !== pathname) {
      // Pathname changed - clear old CSR data
      onClearCSRData();
      setCsrLoadingState({
        listings: true,
        carousels: true,
      });
      setFilteredData({
        listing: [],
        regionDiscipline: [],
        article: [],
        category: [],
      });
    }
    previousPathnameRef.current = pathname;
  }, [pathname, onClearCSRData]);
  
  useEffect(() => {
    // When seoPageData changes, it means new SSR data has loaded
    const currentPageId = seoPageData?.id || seoPageData?.attributes?.id;
    const previousPageId = previousSeoPageDataRef.current?.id || previousSeoPageDataRef.current?.attributes?.id;
    
    if (previousSeoPageDataRef.current && currentPageId !== previousPageId) {
      // New SSR data loaded - clear old CSR data
      onClearCSRData();
      setCsrLoadingState({
        listings: true,
        carousels: true,
      });
      setFilteredData({
        listing: [],
        regionDiscipline: [],
        article: [],
        category: [],
      });
    }
    previousSeoPageDataRef.current = seoPageData;
  }, [seoPageData, onClearCSRData]);
  
  useEffect(() => {
    if (previousLocaleRef.current && previousLocaleRef.current !== currentLocale) {
      // Language changed - clear old data
      // Clear old CSR data from Redux to prevent showing stale content
      onClearCSRData();
      // Reset loading states to show skeletons during language switch
      setCsrLoadingState({
        listings: true,
        carousels: true,
      });
      // Clear filtered data to prevent showing old language content
      setFilteredData({
        listing: [],
        regionDiscipline: [],
        article: [],
        category: [],
      });
    }
    previousLocaleRef.current = currentLocale;
  }, [currentLocale, onClearCSRData]);

  const finalUrlMapping = useMemo(() => {
    // Use the localized URL mappings from our API response
    if (localizedUrlMappings && Object.keys(localizedUrlMappings).length > 0) {
      return localizedUrlMappings;
    }
    
    // During SSR, if we don't have localizedUrlMappings yet, 
    // return empty object to prevent generating incorrect hreflang URLs
    const isSSR = typeof window === 'undefined';
    if (isSSR) {
      return {}; // This will cause alternateHreflangs to return empty array
    }
    
    // Fallback for when no mappings are available (CSR only)
    const supportedLocales = ['en-US', 'it-IT', 'de-DE', 'fr-FR'];
    let currentPath = pathname.replace(/^\/[a-z]{2}-[A-Z]{2}/, '');
    if (!currentPath.startsWith('/')) currentPath = '/' + currentPath;
    currentPath = currentPath.replace(/\/+/g, '/').replace(/^\//, '');
    const mapping = {};
    supportedLocales.forEach(locale => {
      mapping[locale] = currentPath;
    });
    return mapping;
  }, [localizedUrlMappings, pathname]);

  useEffect(() => {
    // Only trigger CSR API calls after SSR data is available and page has rendered
    if (!fetchInProgress && seoPageData && pageType && tag && tag.length > 0) {
  // Starting CSR API calls after SSR data is ready
      
      const listingsFilters = getlistingFilters(pageType, tag);
      if (listingsFilters.discipline) {
        onQueryPopularListings({ slug: listingsFilters.discipline });
      } else if (listingsFilters.region) {
        onQueryPopularListings({ region: listingsFilters.region });
      } else if (listingsFilters.country) {
        onQueryPopularListings({ country: listingsFilters.country });
      } else {
        onQueryPopularListings({});
      }
      const currentLocale = urlParts[0] || 'en-US';
      // Fetch required pageTypes based on current page type
      const requiredPageTypes = getRequiredPageTypes(pageType);
      onFetchSeoPageLinkData(requiredPageTypes, 1000, currentLocale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType, tagString, fetchInProgress, seoPageData]);

  // Update loading states when data arrives
  useEffect(() => {
    // Mark listings as loaded when we have made an API call (not just initial empty array)
    // We'll use the queryPopularListingsInProgress state to determine this
    if (!queryPopularListingsInProgress && popularListings !== undefined && popularListings !== null) {
  // Progressive Loading: Listings loaded
      setCsrLoadingState(prev => ({ ...prev, listings: false }));
    }
  }, [popularListings, queryPopularListingsInProgress]);

  useEffect(() => {
    // Mark carousels as loaded when we have made an API call (not just initial null)
    // We'll use the querySeoInProgress state to determine this
    if (!querySeoInProgress && seoData !== undefined && seoData !== null) {
  // Progressive Loading: Carousel data loaded
      setCsrLoadingState(prev => ({ ...prev, carousels: false }));
    }
  }, [seoData, querySeoInProgress]);

  // Log when data clearing happens  
  useEffect(() => {
    if (!seoData && !popularListings) {
      // CSR data cleared
    }
  }, [seoData, popularListings]);

  // Log loading state changes
  useEffect(() => {
    // Progress/loading state updated
  }, [csrLoadingState, fetchInProgress, seoPageData]);

  useEffect(() => {
    if (seoData && seoData.length > 0 && pageType && tag) {
      const currentLocale = urlParts[0] || 'en-US';
      const filtered = filterRelatedPages(seoData, pageType, tag, currentLocale);
      setFilteredData(filtered);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seoData, pageType, tagString, urlPartsString]);

  const sharedProps = {
    urlParts,
    heroTitle: heroTitle,
    heroDescription: heroDescription,
    titleOnImageTexts: filteredData.regionDiscipline || [],
    titleNotOnImageTexts: filteredData.article || [],
    heroImage: image,
    currentUser,
    config,
    popularListings: popularListings || [], // Provide fallback for components that expect array
    categorySectionData: filteredData.category || [],
    disciplineDetails: filteredData.category || [],
    getShortDescription: (description, maxLength = 150) => {
      if (!description) return '';
      if (typeof description === 'string') {
        return description.length > maxLength
          ? description.substring(0, maxLength) + '...'
          : description;
      }
      return description;
    },
  };

  const buildDynamicComponents = () => {
    const componentStructure = getComponentStructure(pageType);
    const components = [];
    
  // Building components with current loading state
    
    componentStructure.forEach((componentName, index) => {
      const sectionData = section[index];
      switch (componentName) {
        case 'hero':
          components.push({
            __component: 'component.hero-section',
            id: `hero-${index}`,
            title: sectionData?.title,
            description: sectionData?.paragraph,
            heroImage: image,
          });
          break;
        case 'description':
          if (sectionData && (sectionData.title || sectionData.paragraph)) {
            components.push({
              __component: 'component.description-section',
              id: `desc-${index}`,
              title: sectionData.title,
              description: sectionData.paragraph,
              rawDescription: sectionData.paragraph,
            });
          }
          break;
        case 'listing':
          // Treat SSR as loaded when we have preloaded popularListings in the store.
          const effectiveListingsLoaded = (!csrLoadingState.listings) || (isSSR && popularListings && popularListings.length > 0);

          if (!effectiveListingsLoaded) {
            // Show skeleton while listings are loading on client
            components.push({
              __component: 'component.listing-section-skeleton',
              id: `listing-skeleton-${index}`,
              sectionTitle: sectionData?.title,
            });
          } else if (popularListings && Array.isArray(popularListings) && popularListings.length > 0) {
            // Add real listing section (SSR or CSR with data)
            components.push({
              __component: 'component.listing-section',
              id: `carousel-${index}`,
              sectionTitle: sectionData?.title,
              listings: popularListings,
            });
          } else {
            // No listing section - loading done but no data
          }
          // If not loading and no data, don't render anything
          break;
        case 'category':
          if (csrLoadingState.carousels) {
            // Show skeleton while category data is loading
            components.push({
              __component: 'component.category-section-skeleton',
              id: `category-skeleton-${index}`,
              sectionTitle: sectionData?.title,
            });
          } else if (filteredData.category && filteredData.category.length > 0) {
            // Only show actual component if we have data
            components.push({
              __component: 'component.category-section',
              id: `categorySection-${index}`,
              sectionTitle: sectionData?.title,
              disciplineDetails: filteredData.category,
              categorySectionData: filteredData.category,
            });
          }
          // If not loading and no data, don't render anything
          break;
        case 'regionDiscipline':
          if (csrLoadingState.carousels) {
            // Show skeleton while region-discipline data is loading
            components.push({
              __component: 'component.region-discipline-section-skeleton',
              id: `region-discipline-skeleton-${index}`,
              sectionTitle: sectionData?.title,
            });
          } else if (filteredData.regionDiscipline && filteredData.regionDiscipline.length > 0) {
            // Only show actual component if we have data
            components.push({
              __component: 'component.region-discipline-section',
              id: `slider-${index}`,
              sectionTitle: sectionData?.title,
              titleOnImage: true,
              preview: section[0]?.paragraph,
            });
          }
          // If not loading and no data, don't render anything
          break;
        case 'article':
          if (csrLoadingState.carousels) {
            // Show skeleton while article data is loading
            components.push({
              __component: 'component.article-section-skeleton',
              id: `article-skeleton-${index}`,
              sectionTitle: sectionData?.title,
            });
          } else if (filteredData.article && filteredData.article.length > 0) {
            // Only show actual component if we have data
            components.push({
              __component: 'component.article-section',
              id: `article-${index}`,
              sectionTitle: sectionData?.title,
              titleOnImage: false,
              preview: section[0]?.paragraph,
            });
          }
          // If not loading and no data, don't render anything
          break;
        case 'faq':
          if (faq.length > 0) {
            components.push({
              __component: 'component.faq-section',
              id: `faq-${index}`,
              title: sectionData?.title,
              questions: faq,
            });
          }
          break;
        case 'cta':
          components.push({
            __component: 'component.cta-section',
            id: `cta-${index}`,
          });
          break;
        case 'topLocation':
          break;
        default:
          break;
      }
    });
    return components;
  };

  // Determine SSR mode before building components so buildDynamicComponents
  // can safely reference isSSR when deciding whether to render SSR-only parts.
  const isSSR = typeof window === 'undefined';
  // During SSR we already ran loadData on the server, so treat SSR as not loading
  // This allows components that render hidden SEO navs (like listing sections)
  // to render into the initial HTML. On client-side we keep the existing fetchInProgress behavior.
  const isSSRLoading = isSSR ? false : fetchInProgress;

  const dynamicComponents = buildDynamicComponents();

  // Only check for SSR data loading, not CSR API calls
  const hasSSRData = seoPageData && !fetchError;

  // Helper: extract BreadcrumbList.itemListElement from schema if present
  const getBreadcrumbItems = () => {
    const s = schema || pageData?.schema;
    if (!s) return null;
    // Direct BreadcrumbList
    if (s['@type'] === 'BreadcrumbList' && Array.isArray(s.itemListElement)) {
      return s.itemListElement;
    }
    // Graph form: find BreadcrumbList inside @graph
    if (Array.isArray(s['@graph'])) {
      const b = s['@graph'].find(g => g['@type'] === 'BreadcrumbList');
      return b?.itemListElement || null;
    }
    return null;
  };

  const breadcrumbItems = getBreadcrumbItems();

  // ✅ SERVER-SIDE 404 HANDLING:
  // During SSR: loadData() throws 404 errors → Server catches and returns 404 status
  // On client: Only show 404 for partner subdomain (client-side only check)
  // Never render client-side 404 for missing Strapi data - server handles that
  const shouldShow404 = isPartnerSubdomain; // Only partner subdomain check

  if (shouldShow404) {
    return <NotFoundPage />;
  }

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      openGraphType="website"
      description={heroDescription}
      title={heroTitle}
      facebookImages={[{ url: schemaImage, width: 1200, height: 630 }]}
      twitterImages={[{ url: twitterSchemaImage, width: 600, height: 314 }]}
      seoUrlsInAllLang={finalUrlMapping}
      schema={schema}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer currentPage="SeoPage" seoUrlsInAllLang={finalUrlMapping} />}
        footer={<FooterContainer seoUrlsInAllLang={finalUrlMapping} />}
      >
        <div className={css.containerSeo}>
          {!isSSRLoading && hasSSRData && Array.isArray(dynamicComponents) && dynamicComponents.length ? (
            <ul className={css.sections}>
              <li className={css.section}>
                {dynamicComponents.map((component, index) => {
                  const Component = componentMap[component.__component];

                  if (!Component) {
                    return null;
                  }

                  // Keep checks for sections that don't use progressive loading
                  if (
                    component.__component === 'component.description-section' &&
                    !component.title &&
                    !component.description
                  ) {
                    return null;
                  }
                  if (component.__component === 'component.faq-section' && faq.length === 0) {
                    return null;
                  }

                  return (
                    <Component index={index} key={component.id} {...sharedProps} {...component} />
                  );
                })}
              </li>
            </ul>
          ) : (
            <OutdoorSkeletonLoader />
          )}
        </div>

        {/* Footer breadcrumb: render localized breadcrumb from schema if available.
            We intentionally omit the final item (current page) from visible links. */}
        {breadcrumbItems && breadcrumbItems.length > 1 && (
          <div className={css.breadcrumbFooterWrapper}>
            <nav aria-label={intl.formatMessage({ id: 'Breadcrumb.ariaLabel' })}>
              <ol className={css.breadcrumbs}>
                {breadcrumbItems.slice(0, -1).map((item, idx) => (
                  <li key={idx} className={css.breadcrumbItem}>
                    <a href={item.item} className={css.breadcrumbLink}>
                      {item.name}
                    </a>
                    <span className={css.breadcrumbSeparator} aria-hidden>
                      ›
                    </span>
                  </li>
                ))}
              </ol>
            </nav>
          </div>
        )}

      </LayoutSingleColumn>
    </Page>
  );
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onFetchSeoPageLinkData: (pageType, limit, locale) =>
    dispatch(fetchCarouselData(pageType, limit, locale)),
  onQueryPopularListings: params => dispatch(queryPopularListings(params)),
  onClearCSRData: () => dispatch(clearCSRData()),
});

const mapStateToProps = state => {
  const { currentUser } = state.user;
  return {
    seoPageData: state.SeoPage?.seoPageData || null,
    fetchInProgress: state.SeoPage?.fetchInProgress || false,
    fetchError: state.SeoPage?.fetchError || null,
    seoData: state.SeoPage?.seoData, // Don't provide fallback - need to detect null/undefined
    popularListings: state.SeoPage?.popularListings, // Don't provide fallback - need to detect null/undefined
    querySeoInProgress: state.SeoPage?.querySeoInProgress || false,
    queryPopularListingsInProgress: state.SeoPage?.queryPopularListingsInProgress || false,
    localizedUrlMappings: state.SeoPage?.localizedUrlMappings || {},
    currentUser,
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const SeoPage = compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(SeoPageComponent);

export default SeoPage;
